import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { manutencaoService, veiculoService, estoqueService } from '../../services/api';
import Loading from '../../components/common/Loading';

const Manutencoes = () => {
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  // const [manutencaoSelecionada, setManutencaoSelecionada] = useState(null);
  const [filtros, setFiltros] = useState({
    page: 1,
    limit: 10,
    status: '',
    tipo: '',
    veiculo: ''
  });

  const [formData, setFormData] = useState({
    veiculo: '',
    veiculoSubstituto: '',
    tipo: '',
    descricao: '',
    dataInicio: new Date().toISOString().split('T')[0],
    prioridade: 'media',
    quilometragem: '',
    fornecedor: {
      nome: '',
      cnpj: '',
      telefone: '',
      endereco: {
        rua: '',
        numero: '',
        bairro: '',
        cidade: '',
        estado: '',
        cep: ''
      }
    },
    pecasTrocadas: [],
    servicos: [],
    custos: {
      maoDeObra: 0,
      outros: 0
    },
    observacoes: '',
    proximaManutencao: {
      quilometragem: '',
      dataEstimada: '',
      tipo: ''
    }
  });

  const [estoqueBusca, setEstoqueBusca] = useState('');
  const [estoqueBuscaDebounced, setEstoqueBuscaDebounced] = useState('');

  // Debounce para evitar excesso de requisições (rate limit 429)
  useEffect(() => {
    const t = setTimeout(() => {
      setEstoqueBuscaDebounced(estoqueBusca);
    }, 300);

    return () => clearTimeout(t);
  }, [estoqueBusca]);

  // Buscar manutenções
  const { data: manutencoesData, isLoading } = useQuery({
    queryKey: ['manutencoes', filtros],
    queryFn: () => manutencaoService.listar(filtros),
  });

  // Buscar veículos disponíveis
  const { data: veiculosDisponiveis } = useQuery({
    queryKey: ['veiculos-disponiveis'],
    queryFn: () => manutencaoService.veiculosDisponiveis(),
  });

  // Buscar todos os veículos
  const { data: todosVeiculos } = useQuery({
    queryKey: ['todos-veiculos'],
    queryFn: () => veiculoService.listar({ limit: 1000 }),
  });

  // Estoque disponível para seleção das peças trocadas (Fase 2)
  const { data: estoqueItensData, isLoading: estoqueItensLoading } = useQuery({
    queryKey: ['estoque-itens', estoqueBuscaDebounced],
    queryFn: () =>
      estoqueService.listarItens({
        search: estoqueBuscaDebounced,
        tipoConsumo: 'oficina',
        page: 1,
        limit: 20,
        ativo: true,
      }),
    enabled: modalOpen,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });

  // /api/estoque/itens retorna: { success: true, data: [itens], pagination: ... }
  // Aqui `estoqueItensData` é a resposta do axios (React Query recebe `response`),
  // então o array real está em `estoqueItensData.data.data`.
  const estoqueItensDisponiveis = Array.isArray(estoqueItensData?.data?.data)
    ? estoqueItensData.data.data
    : [];

  const manutencoes = manutencoesData?.data?.data?.manutencoes || [];
  const pagination = manutencoesData?.data?.data?.pagination;

  // Mutations
  const criarManutencaoMutation = useMutation({
    mutationFn: manutencaoService.criar,
    onSuccess: () => {
      queryClient.invalidateQueries(['manutencoes']);
      queryClient.invalidateQueries(['veiculos']);
      toast.success('Manutenção criada com sucesso!');
      setModalOpen(false);
      resetForm();
    },
    onError: (error) => {
      toast.error('Erro ao criar manutenção');
      console.error('Erro:', error);
    }
  });

  const finalizarManutencaoMutation = useMutation({
    mutationFn: manutencaoService.finalizar,
    onSuccess: () => {
      queryClient.invalidateQueries(['manutencoes']);
      queryClient.invalidateQueries(['veiculos']);
      toast.success('Manutenção finalizada com sucesso!');
    },
    onError: (error) => {
      toast.error('Erro ao finalizar manutenção');
      console.error('Erro:', error);
    }
  });

  const cancelarManutencaoMutation = useMutation({
    mutationFn: manutencaoService.cancelar,
    onSuccess: () => {
      queryClient.invalidateQueries(['manutencoes']);
      queryClient.invalidateQueries(['veiculos']);
      toast.success('Manutenção cancelada com sucesso!');
    },
    onError: (error) => {
      toast.error('Erro ao cancelar manutenção');
      console.error('Erro:', error);
    }
  });

  const resetForm = () => {
    setFormData({
      veiculo: '',
      veiculoSubstituto: '',
      tipo: '',
      descricao: '',
      dataInicio: new Date().toISOString().split('T')[0],
      prioridade: 'media',
      quilometragem: '',
      fornecedor: {
        nome: '',
        cnpj: '',
        telefone: '',
        endereco: {
          rua: '',
          numero: '',
          bairro: '',
          cidade: '',
          estado: '',
          cep: ''
        }
      },
      pecasTrocadas: [],
      servicos: [],
      custos: {
        maoDeObra: 0,
        outros: 0
      },
      observacoes: '',
      proximaManutencao: {
        quilometragem: '',
        dataEstimada: '',
        tipo: ''
      }
    });
  };

  const handleCreate = () => {
    setModalMode('create');
    resetForm();
    setModalOpen(true);
  };

  const handleEdit = (manutencao) => {
    setModalMode('edit');
    // setManutencaoSelecionada(manutencao);
    setFormData({
      ...manutencao,
      dataInicio: manutencao.dataInicio ? new Date(manutencao.dataInicio).toISOString().split('T')[0] : '',
      proximaManutencao: {
        quilometragem: manutencao.proximaManutencao?.quilometragem || '',
        dataEstimada: manutencao.proximaManutencao?.dataEstimada ? new Date(manutencao.proximaManutencao.dataEstimada).toISOString().split('T')[0] : '',
        tipo: manutencao.proximaManutencao?.tipo || ''
      }
    });
    setModalOpen(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (modalMode === 'create') {
      // Validar que peças trocadas venham do estoque (fase 2/3)
      const pecasInvalidas = (formData.pecasTrocadas || []).some((peca) => {
        const qtd = Number(peca.quantidade || 0);
        return !peca.estoqueItemId || !Number.isFinite(qtd) || qtd < 1;
      });

      if (pecasInvalidas) {
        toast.error('As peças trocadas devem ser selecionadas no estoque.');
        return;
      }
      criarManutencaoMutation.mutate(formData);
    }
  };

  const handleFinalizar = (id) => {
    if (window.confirm('Tem certeza que deseja finalizar esta manutenção?')) {
      finalizarManutencaoMutation.mutate(id);
    }
  };

  const handleCancelar = (id) => {
    if (window.confirm('Tem certeza que deseja cancelar esta manutenção?')) {
      cancelarManutencaoMutation.mutate(id);
    }
  };

  const adicionarPeca = () => {
    setFormData(prev => ({
      ...prev,
      pecasTrocadas: [...prev.pecasTrocadas, {
        nome: '',
        descricao: '',
        estoqueItemId: null,
        codigoBarras: null,
        codigoReferencia: null,
        dataValidadeProxima: null,
        quantidade: 1,
        valorUnitario: 0,
        valorTotal: 0,
        garantia: {
          tipo: 'km',
          valor: '',
          unidade: ''
        }
      }]
    }));
  };

  const selecionarEstoqueItem = async (index, itemId) => {
    const item = estoqueItensDisponiveis.find((i) => i._id === itemId);
    if (!itemId) {
      setFormData((prev) => ({
        ...prev,
        pecasTrocadas: prev.pecasTrocadas.map((peca, i) =>
          i === index
            ? {
              ...peca,
              estoqueItemId: null,
              nome: '',
              codigoBarras: null,
              codigoReferencia: null,
              dataValidadeProxima: null,
              valorUnitario: 0,
              valorTotal: 0,
            }
            : peca
        ),
      }));
      return;
    }

    if (!item) return;

    const valorUnitario = Number(item.valorCompra || 0);

    setFormData((prev) => ({
      ...prev,
      pecasTrocadas: prev.pecasTrocadas.map((peca, i) =>
        i === index
          ? {
            ...peca,
            estoqueItemId: item._id,
            nome: item.nome,
            descricao: item.descricao || '',
            codigoBarras: item.codigoBarras || null,
            codigoReferencia: item.codigoReferencia || null,
            valorUnitario,
            valorTotal: Number(peca.quantidade || 0) * valorUnitario,
          }
          : peca
      ),
    }));

    // Busca validade mais próxima do item (apenas para exibição)
    try {
      const lotesResp = await estoqueService.listarLotes(item._id, { page: 1, limit: 1 });
      const lote = lotesResp?.data?.data?.[0];
      const dataValidadeProxima = lote?.dataValidade ? new Date(lote.dataValidade) : null;

      setFormData((prev) => ({
        ...prev,
        pecasTrocadas: prev.pecasTrocadas.map((peca, i) =>
          i === index
            ? { ...peca, dataValidadeProxima }
            : peca
        ),
      }));
    } catch (e) {
      // Se falhar, não bloqueia a seleção
      console.warn('Falha ao buscar validade do lote:', e);
    }
  };

  const removerPeca = (index) => {
    setFormData(prev => ({
      ...prev,
      pecasTrocadas: prev.pecasTrocadas.filter((_, i) => i !== index)
    }));
  };

  const atualizarPeca = (index, campo, valor) => {
    setFormData(prev => ({
      ...prev,
      pecasTrocadas: prev.pecasTrocadas.map((peca, i) => {
        if (i === index) {
          const pecaAtualizada = { ...peca, [campo]: valor };
          if (campo === 'quantidade' || campo === 'valorUnitario') {
            pecaAtualizada.valorTotal = pecaAtualizada.quantidade * pecaAtualizada.valorUnitario;
          }
          return pecaAtualizada;
        }
        return peca;
      })
    }));
  };

  const adicionarServico = () => {
    setFormData(prev => ({
      ...prev,
      servicos: [...prev.servicos, {
        descricao: '',
        valor: 0
      }]
    }));
  };

  const removerServico = (index) => {
    setFormData(prev => ({
      ...prev,
      servicos: prev.servicos.filter((_, i) => i !== index)
    }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'agendada':
        return 'bg-blue-100 text-blue-800';
      case 'em_andamento':
        return 'bg-yellow-100 text-yellow-800';
      case 'concluida':
        return 'bg-green-100 text-green-800';
      case 'cancelada':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPrioridadeColor = (prioridade) => {
    switch (prioridade) {
      case 'baixa':
        return 'bg-green-100 text-green-800';
      case 'media':
        return 'bg-yellow-100 text-yellow-800';
      case 'alta':
        return 'bg-orange-100 text-orange-800';
      case 'urgente':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatarData = (data) => {
    return new Date(data).toLocaleDateString('pt-BR');
  };

  const formatarValor = (valor) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor);
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <div className="p-6 space-y-6">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between">
        <div>
          <div className="bg-gradient-to-br from-indigo-950 via-indigo-900 to-purple-900 rounded-2xl p-6 md:p-8 text-white shadow-xl mb-6">
            <p className="text-indigo-300 text-sm font-medium uppercase tracking-widest mb-1">Frota</p>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Manutenções</h1>
            <p className="text-indigo-200 mt-1 text-sm">Controle de manutenções preventivas e corretivas</p>
          </div>
          <p className="text-gray-600">Gerencie as manutenções da frota</p>
        </div>
        <button
          onClick={handleCreate}
          className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md transition-colors"
        >
          Nova Manutenção
        </button>
      </div>

      {/* Filtros */}
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Filtros</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              value={filtros.status}
              onChange={(e) => setFiltros(prev => ({ ...prev, status: e.target.value, page: 1 }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos</option>
              <option value="agendada">Agendada</option>
              <option value="em_andamento">Em Andamento</option>
              <option value="concluida">Concluída</option>
              <option value="cancelada">Cancelada</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tipo
            </label>
            <select
              value={filtros.tipo}
              onChange={(e) => setFiltros(prev => ({ ...prev, tipo: e.target.value, page: 1 }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos</option>
              <option value="preventiva">Preventiva</option>
              <option value="corretiva">Corretiva</option>
              <option value="emergencial">Emergencial</option>
              <option value="revisao">Revisão</option>
              <option value="troca_oleo">Troca de Óleo</option>
              <option value="alinhamento">Alinhamento</option>
              <option value="balanceamento">Balanceamento</option>
              <option value="freios">Freios</option>
              <option value="pneus">Pneus</option>
              <option value="bateria">Bateria</option>
              <option value="ar_condicionado">Ar Condicionado</option>
              <option value="eletrica">Elétrica</option>
              <option value="mecanica">Mecânica</option>
              <option value="pintura">Pintura</option>
              <option value="outros">Outros</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Veículo
            </label>
            <select
              value={filtros.veiculo}
              onChange={(e) => setFiltros(prev => ({ ...prev, veiculo: e.target.value, page: 1 }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos</option>
              {todosVeiculos?.data?.data?.map(veiculo => (
                <option key={veiculo._id} value={veiculo._id}>
                  {veiculo.placa} - {veiculo.marca} {veiculo.modelo}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => setFiltros({ page: 1, limit: 10, status: '', tipo: '', veiculo: '' })}
              className="w-full bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md transition-colors"
            >
              Limpar Filtros
            </button>
          </div>
        </div>
      </div>

      {/* Tabela de Manutenções */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Veículo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tipo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Prioridade
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Data Início
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Valor Total
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {manutencoes.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                    Nenhuma manutenção encontrada
                  </td>
                </tr>
              ) : (
                manutencoes.map((manutencao) => (
                  <tr key={manutencao._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {manutencao.veiculo?.placa}
                        </div>
                        <div className="text-sm text-gray-500">
                          {manutencao.veiculo?.marca} {manutencao.veiculo?.modelo}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 capitalize">
                      {manutencao.tipo.replace('_', ' ')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(manutencao.status)}`}>
                        {manutencao.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPrioridadeColor(manutencao.prioridade)}`}>
                        {manutencao.prioridade}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatarData(manutencao.dataInicio)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatarValor(manutencao.custos?.total || 0)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEdit(manutencao)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          Ver
                        </button>
                        {manutencao.status === 'em_andamento' && (
                          <>
                            <button
                              onClick={() => handleFinalizar(manutencao._id)}
                              className="text-green-600 hover:text-green-900"
                            >
                              Finalizar
                            </button>
                            <button
                              onClick={() => handleCancelar(manutencao._id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              Cancelar
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Paginação */}
        {pagination && pagination.totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Mostrando {((pagination.page - 1) * pagination.limit) + 1} a{' '}
              {Math.min(pagination.page * pagination.limit, pagination.total)} de{' '}
              {pagination.total} resultados
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setFiltros(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={!pagination.hasPrevPage}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Anterior
              </button>
              <span className="px-3 py-1 text-sm bg-blue-100 text-blue-800 rounded-md">
                {pagination.page}
              </span>
              <button
                onClick={() => setFiltros(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={!pagination.hasNextPage}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Próximo
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal de Manutenção */}
      {modalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {modalMode === 'create' ? 'Nova Manutenção' : 'Detalhes da Manutenção'}
                </h3>
                <button
                  onClick={() => setModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Informações Básicas */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Veículo *
                    </label>
                    <select
                      value={formData.veiculo}
                      onChange={(e) => setFormData(prev => ({ ...prev, veiculo: e.target.value }))}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Selecione um veículo</option>
                      {todosVeiculos?.data?.data?.map(veiculo => (
                        <option key={veiculo._id} value={veiculo._id}>
                          {veiculo.placa} - {veiculo.marca} {veiculo.modelo}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Veículo Substituto
                    </label>
                    <select
                      value={formData.veiculoSubstituto}
                      onChange={(e) => setFormData(prev => ({ ...prev, veiculoSubstituto: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Nenhum</option>
                      {veiculosDisponiveis?.data?.data?.veiculos?.map(veiculo => (
                        <option key={veiculo._id} value={veiculo._id}>
                          {veiculo.placa} - {veiculo.marca} {veiculo.modelo}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tipo *
                    </label>
                    <select
                      value={formData.tipo}
                      onChange={(e) => setFormData(prev => ({ ...prev, tipo: e.target.value }))}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Selecione o tipo</option>
                      <option value="preventiva">Preventiva</option>
                      <option value="corretiva">Corretiva</option>
                      <option value="emergencial">Emergencial</option>
                      <option value="revisao">Revisão</option>
                      <option value="troca_oleo">Troca de Óleo</option>
                      <option value="alinhamento">Alinhamento</option>
                      <option value="balanceamento">Balanceamento</option>
                      <option value="freios">Freios</option>
                      <option value="pneus">Pneus</option>
                      <option value="bateria">Bateria</option>
                      <option value="ar_condicionado">Ar Condicionado</option>
                      <option value="eletrica">Elétrica</option>
                      <option value="mecanica">Mecânica</option>
                      <option value="pintura">Pintura</option>
                      <option value="outros">Outros</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Prioridade
                    </label>
                    <select
                      value={formData.prioridade}
                      onChange={(e) => setFormData(prev => ({ ...prev, prioridade: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="baixa">Baixa</option>
                      <option value="media">Média</option>
                      <option value="alta">Alta</option>
                      <option value="urgente">Urgente</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Data de Início *
                    </label>
                    <input
                      type="date"
                      value={formData.dataInicio}
                      onChange={(e) => setFormData(prev => ({ ...prev, dataInicio: e.target.value }))}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Quilometragem *
                    </label>
                    <input
                      type="number"
                      value={formData.quilometragem}
                      onChange={(e) => setFormData(prev => ({ ...prev, quilometragem: e.target.value }))}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descrição *
                  </label>
                  <textarea
                    value={formData.descricao}
                    onChange={(e) => setFormData(prev => ({ ...prev, descricao: e.target.value }))}
                    required
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Fornecedor */}
                <div className="border-t pt-4">
                  <h4 className="text-md font-semibold text-gray-900 mb-3">Fornecedor</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nome *
                      </label>
                      <input
                        type="text"
                        value={formData.fornecedor.nome}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          fornecedor: { ...prev.fornecedor, nome: e.target.value }
                        }))}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        CNPJ
                      </label>
                      <input
                        type="text"
                        value={formData.fornecedor.cnpj}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          fornecedor: { ...prev.fornecedor, cnpj: e.target.value }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Telefone
                      </label>
                      <input
                        type="text"
                        value={formData.fornecedor.telefone}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          fornecedor: { ...prev.fornecedor, telefone: e.target.value }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Peças Trocadas */}
                <div className="border-t pt-4">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="text-md font-semibold text-gray-900">Peças Trocadas</h4>
                    <button
                      type="button"
                      onClick={adicionarPeca}
                      disabled={estoqueItensLoading || estoqueItensDisponiveis.length === 0}
                      className={`px-3 py-1 rounded-md text-sm ${
                        estoqueItensLoading || estoqueItensDisponiveis.length === 0
                          ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                          : 'bg-green-600 hover:bg-green-700 text-white'
                      }`}
                    >
                      Adicionar Peça
                    </button>
                  </div>

                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Buscar no estoque
                    </label>
                    <input
                      type="text"
                      value={estoqueBusca}
                      onChange={(e) => setEstoqueBusca(e.target.value)}
                      placeholder="Nome, ref ou código de barras..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  {formData.pecasTrocadas.map((peca, index) => (
                    <div key={index} className="border rounded-lg p-4 mb-3">
                      <div className="flex justify-between items-center mb-3">
                        <h5 className="font-medium text-gray-900">Peça {index + 1}</h5>
                        <button
                          type="button"
                          onClick={() => removerPeca(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          Remover
                        </button>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Item / Peça *
                          </label>
                          <select
                            value={peca.estoqueItemId || ''}
                            onChange={(e) => selecionarEstoqueItem(index, e.target.value)}
                            required
                            disabled={estoqueItensLoading}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="">Selecione no estoque</option>
                            {peca.estoqueItemId && !estoqueItensDisponiveis.some((i) => i._id === peca.estoqueItemId) ? (
                              <option value={peca.estoqueItemId}>{peca.nome || 'Item'}</option>
                            ) : null}
                            {estoqueItensDisponiveis.map((item) => (
                              <option key={item._id} value={item._id}>
                                {item.nome}
                                {item.codigoReferencia ? ` - ${item.codigoReferencia}` : ''}
                              </option>
                            ))}
                          </select>
                          <div className="mt-2 text-xs text-gray-600 space-y-1">
                            {peca.codigoBarras ? <div>Código de barras: {peca.codigoBarras}</div> : null}
                            {peca.codigoReferencia ? <div>Ref: {peca.codigoReferencia}</div> : null}
                            {peca.dataValidadeProxima ? (
                              <div>Validade próxima: {new Date(peca.dataValidadeProxima).toLocaleDateString('pt-BR')}</div>
                            ) : null}
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Quantidade *
                          </label>
                          <input
                            type="number"
                            min="1"
                            value={peca.quantidade}
                            onChange={(e) => atualizarPeca(index, 'quantidade', parseInt(e.target.value))}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Valor Unitário *
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={peca.valorUnitario}
                            readOnly
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Valor Total
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            value={peca.valorTotal}
                            readOnly
                            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Garantia (KM)
                          </label>
                          <input
                            type="number"
                            value={peca.garantia.valor}
                            onChange={(e) => atualizarPeca(index, 'garantia', { ...peca.garantia, valor: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Serviços */}
                <div className="border-t pt-4">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="text-md font-semibold text-gray-900">Serviços</h4>
                    <button
                      type="button"
                      onClick={adicionarServico}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-md text-sm"
                    >
                      Adicionar Serviço
                    </button>
                  </div>
                  
                  {formData.servicos.map((servico, index) => (
                    <div key={index} className="border rounded-lg p-4 mb-3">
                      <div className="flex justify-between items-center mb-3">
                        <h5 className="font-medium text-gray-900">Serviço {index + 1}</h5>
                        <button
                          type="button"
                          onClick={() => removerServico(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          Remover
                        </button>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Descrição *
                          </label>
                          <input
                            type="text"
                            value={servico.descricao}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              servicos: prev.servicos.map((s, i) => 
                                i === index ? { ...s, descricao: e.target.value } : s
                              )
                            }))}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Valor *
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={servico.valor}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              servicos: prev.servicos.map((s, i) => 
                                i === index ? { ...s, valor: parseFloat(e.target.value) } : s
                              )
                            }))}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Custos */}
                <div className="border-t pt-4">
                  <h4 className="text-md font-semibold text-gray-900 mb-3">Custos</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Mão de Obra
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.custos.maoDeObra}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          custos: { ...prev.custos, maoDeObra: parseFloat(e.target.value) || 0 }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Outros Custos
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.custos.outros}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          custos: { ...prev.custos, outros: parseFloat(e.target.value) || 0 }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Próxima Manutenção */}
                <div className="border-t pt-4">
                  <h4 className="text-md font-semibold text-gray-900 mb-3">Próxima Manutenção</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Quilometragem
                      </label>
                      <input
                        type="number"
                        value={formData.proximaManutencao.quilometragem}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          proximaManutencao: { ...prev.proximaManutencao, quilometragem: e.target.value }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Data Estimada
                      </label>
                      <input
                        type="date"
                        value={formData.proximaManutencao.dataEstimada}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          proximaManutencao: { ...prev.proximaManutencao, dataEstimada: e.target.value }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Tipo
                      </label>
                      <input
                        type="text"
                        value={formData.proximaManutencao.tipo}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          proximaManutencao: { ...prev.proximaManutencao, tipo: e.target.value }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Observações
                  </label>
                  <textarea
                    value={formData.observacoes}
                    onChange={(e) => setFormData(prev => ({ ...prev, observacoes: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Botões */}
                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => setModalOpen(false)}
                    className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md transition-colors"
                  >
                    Cancelar
                  </button>
                  {modalMode === 'create' && (
                    <button
                      type="submit"
                      disabled={criarManutencaoMutation.isPending}
                      className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md transition-colors disabled:opacity-50"
                    >
                      {criarManutencaoMutation.isPending ? 'Criando...' : 'Criar Manutenção'}
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Manutencoes;
